// ADDED: the picker behind the "Add" button on a model card. Lists the user's collections
// and links the chosen catalog model into it.
import React, { useState, useEffect } from 'react';

import axios, { getErrorMessage } from '../../api/axios';

export default function AddToCollectionModal({ isVisible, onClose, car }) {
    const [collections, setCollections] = useState([]);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isBusy, setIsBusy] = useState(false);
    const [newCollectionName, setNewCollectionName] = useState('');

    // Reload the list each time the modal opens, so newly created collections show up
    useEffect(() => {
        if (!isVisible) return;
        setMessage('');
        setError('');
        axios.get('/collections')
            .then((response) => setCollections(response.data.collections))
            .catch((err) => setError(getErrorMessage(err, 'Could not load your collections.')));
    }, [isVisible]);

    const handleAdd = async (collectionId) => {
        setIsBusy(true);
        setError('');
        try {
            await axios.post(`/collections/${collectionId}/cars`, { carId: car.id });
            setMessage('Added to your collection.');
        } catch (err) {
            // 409 means the model is already in that collection, which is worth saying plainly
            setError(getErrorMessage(err, 'Could not add the model.'));
        } finally {
            setIsBusy(false);
        }
    };

    // Convenience: make a collection and drop the car straight into it
    const handleCreateAndAdd = async (e) => {
        e.preventDefault();
        if (!newCollectionName.trim()) return;
        setIsBusy(true);
        setError('');
        try {
            const response = await axios.post('/collections', { collectionName: newCollectionName.trim() });
            await axios.post(`/collections/${response.data.collection.id}/cars`, { carId: car.id });
            setNewCollectionName('');
            setMessage('Collection created and model added.');
            const refreshed = await axios.get('/collections');
            setCollections(refreshed.data.collections);
        } catch (err) {
            setError(getErrorMessage(err, 'Could not create the collection.'));
        } finally {
            setIsBusy(false);
        }
    };

    if (!isVisible || !car) return null;

    // MODIFIED (styling): shared card/button/input classes so the modal follows the theme
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="w-full max-w-md cv-card overflow-hidden max-h-[90vh] overflow-y-auto animate-fade-up">
                <div className="cv-stripe" />
                <div className="flex justify-between items-start p-4" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <div className="min-w-0">
                        <h2 className="cv-display text-xl">Add to a collection</h2>
                        <p className="text-sm cv-muted truncate">{car.name}</p>
                    </div>
                    <button onClick={onClose} aria-label="Close"
                        className="cv-muted hover:text-brand-flame text-2xl leading-none shrink-0 px-2 transition duration-300">&times;</button>
                </div>

                <div className="p-4 space-y-3">
                    {message && <p className="cv-alert-success">{message}</p>}
                    {error && <p className="cv-alert-error">{error}</p>}

                    {collections.length === 0 && !error &&
                        <p className="text-sm cv-muted">You have no collections yet. Create one below.</p>
                    }

                    {collections.map((collection) => (
                        <button key={collection.id} disabled={isBusy}
                            onClick={() => handleAdd(collection.id)}
                            className="w-full flex justify-between items-center gap-2 rounded-xl px-4 py-3 text-left transition duration-300 hover:border-brand-flame disabled:opacity-60"
                            style={{ border: '1px solid var(--border-subtle)', backgroundColor: 'var(--surface-sunken)' }}>
                            <span className="cv-strong truncate">{collection.collectionName}</span>
                            <span className="text-xs cv-muted shrink-0">{collection.carCount} models</span>
                        </button>
                    ))}

                    <form onSubmit={handleCreateAndAdd} className="pt-3 flex flex-col sm:flex-row gap-2"
                          style={{ borderTop: '1px solid var(--border-subtle)' }}>
                        <input type="text" value={newCollectionName} autoComplete="off"
                            onChange={(e) => setNewCollectionName(e.target.value)}
                            placeholder="Or create a new collection"
                            className="cv-input flex-1" />
                        <button type="submit" disabled={isBusy} className="cv-btn cv-btn-primary whitespace-nowrap">
                            Create &amp; add
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
